import { z } from 'zod';
import { COMPLAINANT_TYPE } from '../constants/business/complaint';
import {
    type ExtractedField,
    llmExtractedField,
    llmSection,
    mapEnumField,
    mapProductCodesField,
    matchAllowed,
    normaliseDateWithinBound,
    PREFILL_AUDIT_SCHEMA_VERSION,
    PrefillAuditInputSchema,
    passthrough,
    prefillFieldEntrySchema,
    strictExtractedField,
} from './ai-extraction';

/**
 * Shared schemas for AI-extracted complaint draft data.
 *
 * Generic primitives (the `extractedField` wrappers, confidence/source enums,
 * enum-matching helpers, prefill state machine, audit-input schema) now live in
 * `./ai-extraction` and are shared with the incident extraction stack. This
 * file keeps the complaint-specific section tree, the loose→strict translation,
 * and the persisted record/audit schemas — and re-exports the generic names it
 * historically owned for backward compatibility.
 *
 * Two schemas live here so we can be lenient with the LLM and strict with our
 * own storage:
 *
 * 1. `ComplaintExtractionLooseLlmSchema` — what the LLM is asked to return.
 * 2. `ComplaintExtractionStrictSchema` — the shape we actually persist, built
 *    by mapping the loose output through `translateLooseExtractionToStrict()`.
 *
 * Browser-safe by construction — no `@server/*` imports.
 */

// Re-export the generic primitives that used to live here so existing
// `@shared/schemas/complaint-extraction` imports keep resolving.
export {
    EXTRACTION_CONFIDENCE,
    EXTRACTION_SOURCE_FIELD,
    type ExtractedField,
    type ExtractionConfidence,
    type ExtractionSourceField,
    PREFILL_AUDIT_SCHEMA_VERSION,
    PREFILL_FIELD_STATE,
    type PrefillFieldState,
} from './ai-extraction';

// The client-submitted audit input is entity-agnostic — alias the shared
// schema/type so callers referencing the complaint-named symbols still work.
export const ComplaintPrefillAuditInputSchema = PrefillAuditInputSchema;
export type ComplaintPrefillAuditInput = z.infer<typeof PrefillAuditInputSchema>;

// ──────────────────────────────────────────────────────────────────────────
// LLM-facing schema. Plain optional objects + leaves — NO unions, NO
// `.transform()`. `.catch({})` at the root keeps us resilient if the model
// returns something unexpected.
// ──────────────────────────────────────────────────────────────────────────

const llmString = z.string().min(1).max(500);

export const ComplaintExtractionLooseLlmSchema = z
    .object({
        complainant: llmSection({
            firstName: llmExtractedField(llmString),
            lastName: llmExtractedField(llmString),
            type: llmExtractedField(llmString),
            gender: llmExtractedField(llmString),
            age: llmExtractedField(llmString),
            postcode: llmExtractedField(llmString),
            relationship: llmExtractedField(llmString),
        }),
        contact: llmSection({
            channel: llmExtractedField(llmString),
            preferredChannel: llmExtractedField(llmString),
            preferredChannelValue: llmExtractedField(llmString),
        }),
        classification: llmSection({
            // Cap matches the form's "Select up to 3 issues" copy.
            issues: llmExtractedField(z.array(llmString).max(3)),
            afsLicenseeRelated: llmExtractedField(llmString),
        }),
        description: llmSection({
            incidentDescription: llmExtractedField(z.string().min(1).max(8000)),
            complainantSolution: llmExtractedField(z.string().min(1).max(2000)),
        }),
        claim: llmSection({
            externalClaimId: llmExtractedField(llmString),
            policyNo: llmExtractedField(llmString),
            // Product LABELS picked from `constraints.products` (not codes). A
            // field named "productCodes" primed the model to emit a row-number or
            // omit the field entirely; asking for labels into a label-named field
            // makes it reliably return the right products, which the translate
            // then resolves to numeric ASIC codes. Cap matches MAX_ASIC_PRODUCT_SLOTS.
            products: llmExtractedField(z.array(llmString).max(3)),
            brand: llmExtractedField(llmString),
        }),
        // Intake/handling metadata. `dtReceived` is the date the complaint was
        // received/lodged (ISO; bounded + parsed in translate). For an auto-detect
        // EMAIL/SMS the client's deterministic message-timestamp prefill wins over
        // this; for a pasted/uploaded document it is the only date source.
        handling: llmSection({
            dtReceived: llmExtractedField(llmString),
            referredByAfca: llmExtractedField(llmString),
            afcaReferenceNo: llmExtractedField(llmString),
        }),
    })
    .catch({});

export type ComplaintExtractionLooseLlmOutput = z.infer<typeof ComplaintExtractionLooseLlmSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Strict schema (persisted shape). Real enums; constructed via
// translateLooseExtractionToStrict().
// ──────────────────────────────────────────────────────────────────────────

const ComplainantTypeValues = Object.values(COMPLAINANT_TYPE);

const strictComplainantSection = z
    .object({
        firstName: strictExtractedField(z.string().trim().min(1)).optional(),
        lastName: strictExtractedField(z.string().trim().min(1)).optional(),
        type: strictExtractedField(z.enum(ComplainantTypeValues as [string, ...string[]])).optional(),
        gender: strictExtractedField(z.string().trim().min(1)).optional(),
        age: strictExtractedField(z.string().trim().min(1)).optional(),
        postcode: strictExtractedField(z.string().trim().min(1)).optional(),
        relationship: strictExtractedField(z.string().trim().min(1)).optional(),
    })
    .strict()
    .optional();

const strictContactSection = z
    .object({
        channel: strictExtractedField(z.string().trim().min(1)).optional(),
        preferredChannel: strictExtractedField(z.string().trim().min(1)).optional(),
        preferredChannelValue: strictExtractedField(z.string().trim().min(1)).optional(),
    })
    .strict()
    .optional();

const strictClassificationSection = z
    .object({
        issues: strictExtractedField(z.array(z.string().trim().min(1)).max(3)).optional(),
        afsLicenseeRelated: strictExtractedField(z.string().trim().min(1)).optional(),
    })
    .strict()
    .optional();

const strictDescriptionSection = z
    .object({
        incidentDescription: strictExtractedField(z.string().trim().min(1).max(8000)).optional(),
        complainantSolution: strictExtractedField(z.string().trim().min(1).max(2000)).optional(),
    })
    .strict()
    .optional();

const strictClaimSection = z
    .object({
        externalClaimId: strictExtractedField(z.string().trim().min(1)).optional(),
        policyNo: strictExtractedField(z.string().trim().min(1)).optional(),
        // Numeric ASIC product codes, aligned with the form's
        // `AsicProductCascader` (`number[]`) and `asicProductsSchema` on the
        // server (`z.number().array().max(3)`). The translation step converts
        // the LLM's string codes to numbers.
        productCodes: strictExtractedField(z.array(z.number().int().positive()).max(3)).optional(),
        brand: strictExtractedField(z.string().trim().min(1)).optional(),
    })
    .strict()
    .optional();

const strictHandlingSection = z
    .object({
        // ISO date string; the client maps it to a `Date` via the field-map
        // `toFormValue`. Bounded to ≤ received date in translate.
        dtReceived: strictExtractedField(z.string().trim().min(1)).optional(),
        // "Yes" / "No" / "Unknown"; the client maps to the form's boolean switch.
        referredByAfca: strictExtractedField(z.string().trim().min(1)).optional(),
        afcaReferenceNo: strictExtractedField(z.string().trim().min(1)).optional(),
    })
    .strict()
    .optional();

export const ComplaintExtractionStrictSchema = z
    .object({
        complainant: strictComplainantSection,
        contact: strictContactSection,
        classification: strictClassificationSection,
        description: strictDescriptionSection,
        claim: strictClaimSection,
        handling: strictHandlingSection,
    })
    .strict();

export type ComplaintExtractionFields = z.infer<typeof ComplaintExtractionStrictSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Persisted record shape (what lives on
// MessageClassification.complaintExtraction)
// ──────────────────────────────────────────────────────────────────────────

// Bumped 1 → 2 when the `handling` section (dtReceived / referredByAfca /
// afcaReferenceNo) was added. Stale v1 records regenerate on next view.
export const COMPLAINT_EXTRACTION_SCHEMA_VERSION = 2;

export const ComplaintExtractionRecordSchema = z
    .object({
        schemaVersion: z.literal(COMPLAINT_EXTRACTION_SCHEMA_VERSION),
        extractedAt: z.string().datetime(),
        modelUsed: z.string().min(1),
        extractionCorrelationId: z.string().min(1),
        promptTokens: z.number().int().nullable(),
        completionTokens: z.number().int().nullable(),
        fields: ComplaintExtractionStrictSchema,
    })
    .strict();

export type ComplaintExtractionRecord = z.infer<typeof ComplaintExtractionRecordSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Persisted audit record (what lives on Complaint.prefillAudit). Built
// server-side via `buildComplaintPrefillAudit()`. Snapshot — does NOT
// pointer-reference the mutable MessageClassification.complaintExtraction.
// ──────────────────────────────────────────────────────────────────────────

export const ComplaintPrefillAuditSchema = z
    .object({
        schemaVersion: z.literal(PREFILL_AUDIT_SCHEMA_VERSION),
        // Exactly one source id is set (server-stamped, never client-supplied):
        // the auto-detect message (messageClassificationId) or the paste/upload
        // source (aiPrefillSourceId). BigInts are pre-stringified at the create
        // boundary (Prisma JSON can't round-trip native BigInt).
        messageClassificationId: z.string().min(1).optional(),
        aiPrefillSourceId: z.string().min(1).optional(),
        extractionCorrelationId: z.string().min(1),
        extractionSchemaVersion: z.literal(COMPLAINT_EXTRACTION_SCHEMA_VERSION),
        extractedAt: z.string().datetime(),
        modelUsed: z.string().min(1),
        fields: z.record(z.string(), prefillFieldEntrySchema),
    })
    .strict()
    .refine((d) => Boolean(d.messageClassificationId) !== Boolean(d.aiPrefillSourceId), {
        message: 'Exactly one of messageClassificationId or aiPrefillSourceId must be set.',
    });

export type ComplaintPrefillAuditRecord = z.infer<typeof ComplaintPrefillAuditSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Loose → strict translation. Drops unknown enum values per field.
// ──────────────────────────────────────────────────────────────────────────

export interface ExtractionConstraints {
    // Mutable arrays so the value can flow directly into the LLM prompt's
    // `z.array(z.string())` input (which infers mutable). Callers shouldn't
    // mutate; the convention is "build once at call site".
    relationships: string[];
    /**
     * Full "Category - Issue" labels (e.g. "Service - Delay in claim handling"),
     * exactly as the form's `ComplaintIssueSelect` expects. The LLM picks from
     * these directly — giving it the category context it needs to disambiguate —
     * and `mapIssuesField` validates membership.
     */
    issues: string[];
    channels: string[];
    complainantTypes: string[];
    afsLicenseeRelated: string[];
    genders: string[];
    ages: string[];
    /** The complainant-solution dropdown options (the LLM prefers one, else free text). */
    complainantSolutions: string[];
    brands: string[];
    /**
     * ASIC products as `{code,label}` pairs so the LLM can map a product *name*
     * in the text (e.g. "motor insurance") to its numeric code. The LLM returns
     * the numeric code(s); `mapProductCodesField` validates against these codes.
     */
    products: { code: string; label: string }[];
    /** Upper bound for `dtReceived` (the message received date / now). ISO string. */
    maxDtReceived?: string;
}

/** Allowed values for the AFCA-referral boolean-ish field. */
const REFERRED_BY_AFCA_VALUES = ['Yes', 'No', 'Unknown'] as const;

/**
 * Validate the LLM's issue picks against the allowed full "Category - Issue"
 * label set (case-insensitive), returning the canonical casing. Unknown values
 * are dropped; capped at 3 (the form's max). Exported so the focused
 * issue-matcher (server) can reuse the same validation.
 */
export function mapIssuesField(
    field: ExtractedField<string[]> | undefined,
    allowed: readonly string[],
): ExtractedField<string[]> | undefined {
    if (!field) return undefined;
    const canonicalByLower = new Map(allowed.map((label) => [label.toLowerCase(), label]));
    const seen = new Set<string>();
    const matched: string[] = [];
    for (const v of field.value) {
        const canonical = canonicalByLower.get(v.trim().toLowerCase());
        if (!canonical) continue;
        if (seen.has(canonical)) continue;
        seen.add(canonical);
        matched.push(canonical);
    }
    if (matched.length === 0) return undefined;
    // Cap at 3 — matches MAX issue slots on the form and the LLM cap.
    return { ...field, value: matched.slice(0, 3) };
}

/**
 * Classify a free-text contact detail as email- or phone-shaped so the
 * deterministic backstop can fill `preferredChannel` when the model extracted a
 * contact detail (`preferredChannelValue`) but not its channel — the
 * filled-value-with-empty-dropdown case that prompt-only inference keeps
 * missing. Returns the *canonical* channel label from `allowedChannels` (so we
 * never emit a label the org's channel set doesn't contain), or `undefined` when
 * the value is neither shape or the matching channel isn't allowed.
 */
export function derivePreferredChannelLabel(value: string, allowedChannels: readonly string[]): string | undefined {
    const v = value.trim();
    if (!v) return undefined;
    // Email — the address may sit inside surrounding text ("Email: a@b.com",
    // "Name <a@b.com>"), so search for the token rather than anchoring the whole
    // string; the dotted domain makes a spurious match unlikely.
    if (/[^\s@]+@[^\s@]+\.[^\s@]+/.test(v)) return matchAllowed('Email', allowedChannels);
    // Phone — tolerate a leading contact-channel label prefix ("Phone: …",
    // "Mobile: …"), then require the remainder to be ONLY a phone number (6–15
    // digits, optional leading '+', after stripping spaces/dashes/dots/parentheses).
    // Anchoring the remainder stops a number embedded in free text from
    // misclassifying as a phone. The label set is restricted to channel words (not
    // any "word:" prefix) so a label-prefixed reference like "Policy: 4839201"
    // keeps its label and fails the anchored digit check rather than being stripped
    // down to a bare number.
    const digits = v.replace(/^(?:phone|mobile|tel|cell|landline)\s*:\s*/i, '').replace(/[\s\-().]/g, '');
    if (/^\+?\d{6,15}$/.test(digits)) return matchAllowed('Phone', allowedChannels);
    return undefined;
}

export function translateLooseExtractionToStrict(
    loose: ComplaintExtractionLooseLlmOutput,
    constraints: ExtractionConstraints,
): ComplaintExtractionFields {
    const out: ComplaintExtractionFields = {};

    if (loose.complainant) {
        const c = loose.complainant;
        const section: NonNullable<ComplaintExtractionFields['complainant']> = {};
        const firstName = passthrough(c.firstName);
        const lastName = passthrough(c.lastName);
        const postcode = passthrough(c.postcode);
        if (firstName) section.firstName = firstName;
        if (lastName) section.lastName = lastName;
        if (postcode) section.postcode = postcode;

        const type = mapEnumField(c.type, constraints.complainantTypes);
        if (type) section.type = type;

        // Gender/Age only meaningful when complainant is Individual or couple
        // (mirrors server-side complaint schema superRefine).
        if (type?.value === COMPLAINANT_TYPE.INDIVIDUAL) {
            const gender = mapEnumField(c.gender, constraints.genders);
            const age = mapEnumField(c.age, constraints.ages);
            if (gender) section.gender = gender;
            if (age) section.age = age;
        }

        const relationship = mapEnumField(c.relationship, constraints.relationships);
        if (relationship) section.relationship = relationship;

        if (Object.keys(section).length > 0) out.complainant = section;
    }

    if (loose.contact) {
        const c = loose.contact;
        const section: NonNullable<ComplaintExtractionFields['contact']> = {};
        const channel = mapEnumField(c.channel, constraints.channels);
        let preferredChannel = mapEnumField(c.preferredChannel, constraints.channels);
        const preferredChannelValue = passthrough(c.preferredChannelValue);

        // Deterministic backstop: when the model extracted a contact detail but
        // left the preferred channel empty, derive the channel from the detail's
        // shape (an email address → "Email", a phone number → "Phone"). This
        // fixes the filled-value-with-empty-dropdown case the prompt's
        // explicit-preference rule keeps missing. We reuse the value's source
        // attribution (the channel is inferred from the same evidence — the
        // verified quote re-validates intact downstream), only emit a label the
        // org's channel set contains, and never override an explicit model pick.
        if (!preferredChannel && preferredChannelValue) {
            const derivedLabel = derivePreferredChannelLabel(preferredChannelValue.value, constraints.channels);
            if (derivedLabel) {
                preferredChannel = {
                    value: derivedLabel,
                    // The channel is deterministically inferred, not stated by the
                    // model, so it is never a higher-than-MEDIUM signal even when
                    // the contact detail it was derived from was HIGH-confidence.
                    confidence:
                        preferredChannelValue.confidence === 'HIGH' ? 'MEDIUM' : preferredChannelValue.confidence,
                    sourceField: preferredChannelValue.sourceField,
                    sourceQuote: preferredChannelValue.sourceQuote,
                };
            }
        }

        if (channel) section.channel = channel;
        if (preferredChannel) section.preferredChannel = preferredChannel;
        if (preferredChannelValue) section.preferredChannelValue = preferredChannelValue;
        if (Object.keys(section).length > 0) out.contact = section;
    }

    if (loose.classification) {
        const c = loose.classification;
        const section: NonNullable<ComplaintExtractionFields['classification']> = {};
        // Validate the LLM's full "Category - Issue" picks against the allow-list.
        const issues = mapIssuesField(passthrough(c.issues), constraints.issues);
        const afsLicenseeRelated = mapEnumField(c.afsLicenseeRelated, constraints.afsLicenseeRelated);
        if (issues) section.issues = issues;
        if (afsLicenseeRelated) section.afsLicenseeRelated = afsLicenseeRelated;
        if (Object.keys(section).length > 0) out.classification = section;
    }

    if (loose.description) {
        const d = loose.description;
        const section: NonNullable<ComplaintExtractionFields['description']> = {};
        const incidentDescription = passthrough(d.incidentDescription);
        const complainantSolution = passthrough(d.complainantSolution);
        if (incidentDescription) section.incidentDescription = incidentDescription;
        if (complainantSolution) section.complainantSolution = complainantSolution;
        if (Object.keys(section).length > 0) out.description = section;
    }

    if (loose.claim) {
        const c = loose.claim;
        const section: NonNullable<ComplaintExtractionFields['claim']> = {};
        const externalClaimId = passthrough(c.externalClaimId);
        const policyNo = passthrough(c.policyNo);
        if (externalClaimId) section.externalClaimId = externalClaimId;
        if (policyNo) section.policyNo = policyNo;

        // Resolve LLM product labels (or codes) to the numeric ASIC codes
        // `asicProductsSchema` requires ([44]), validating against the allow-list.
        const productCodes = mapProductCodesField(passthrough(c.products), constraints.products);
        if (productCodes) section.productCodes = productCodes;

        const brand = mapEnumField(c.brand, constraints.brands);
        if (brand) section.brand = brand;

        if (Object.keys(section).length > 0) out.claim = section;
    }

    if (loose.handling) {
        const h = loose.handling;
        const section: NonNullable<ComplaintExtractionFields['handling']> = {};
        const dtReceived = normaliseDateWithinBound(passthrough(h.dtReceived), constraints.maxDtReceived);
        if (dtReceived) section.dtReceived = dtReceived;

        const referredByAfca = h.referredByAfca
            ? (() => {
                  const match = matchAllowed(h.referredByAfca?.value, REFERRED_BY_AFCA_VALUES);
                  return match ? { ...h.referredByAfca, value: match } : undefined;
              })()
            : undefined;
        if (referredByAfca) section.referredByAfca = referredByAfca;

        const afcaReferenceNo = passthrough(h.afcaReferenceNo);
        if (afcaReferenceNo) section.afcaReferenceNo = afcaReferenceNo;

        if (Object.keys(section).length > 0) out.handling = section;
    }

    return out;
}
